import React, { useEffect, useState } from "react";
import {
  Box,
  useMediaQuery,
  Theme,
  Tooltip,
  ListItem,
  ListItemIcon,
  useTheme,
  Drawer,
  Backdrop,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ProfileDropdownMenu from "../../components/ProfileDropdownMenu";
import { usePWA } from "../../hooks/usePWA";
import { isSuperAdminRole } from "../../utils/userUtils";
import { useFeatureFlags, FlagKey } from "../../contexts/FeatureFlagsContext";

// Imagens
import logoJess from "../../assets/logos/Logo_jesss.svg";
import chatClientIcon from "../../assets/Sidebaricons/chatclient.svg";
import chatClientIconActive from "../../assets/Sidebaricons/chatclienroxo.svg";
import chatJessIcon from "../../assets/Sidebaricons/chatjesss.svg";
import chatJessIconActive from "../../assets/Sidebaricons/chatjesssroxo.svg";
import taskBoardIcon from "../../assets/Sidebaricons/taskboard.svg";
import taskBoardIconActive from "../../assets/Sidebaricons/taskboardroxo.svg";
import sessionIcon from "../../assets/Sidebaricons/session.svg";
import sessionIconActive from "../../assets/Sidebaricons/sessionroxo.svg";
import connectorsIcon from "../../assets/Sidebaricons/connectors.svg";
import connectorsIconActive from "../../assets/Sidebaricons/connectorsroxo.svg";
import connectionsIcon from "../../assets/Sidebaricons/connection.svg";
import connectionsIconActive from "../../assets/Sidebaricons/connectionroxo.svg";
import downloadIcon from "../../assets/Sidebaricons/download.svg";

interface SideMenuProps {
  onChangeLayoutMode: (value: any) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const navItems: {
  to: string;
  iconSrc?: string;
  iconSrcActive?: string;
  icon?: React.ElementType;
  labelKey: string;
  flagKey?: FlagKey;
}[] = [
  {
    to: "/chat-client",
    iconSrc: chatClientIcon,
    iconSrcActive: chatClientIconActive,
    labelKey: "sidebar.chatClient",
    flagKey: "chat_client",
  },
  {
    to: "/task-board",
    iconSrc: taskBoardIcon,
    iconSrcActive: taskBoardIconActive,
    labelKey: "sidebar.taskBoard",
    flagKey: "task_board",
  },
  {
    to: "/channel-session",
    iconSrc: sessionIcon,
    iconSrcActive: sessionIconActive,
    labelKey: "sidebar.channelSession",
    flagKey: "channel_session",
  },
  {
    to: "/workflows",
    icon: AccountTreeIcon,
    labelKey: "sidebar.workflows",
    flagKey: "workflows",
  },
  {
    to: "/connectors",
    iconSrc: connectorsIcon,
    iconSrcActive: connectorsIconActive,
    labelKey: "sidebar.connectors",
    flagKey: "connectors",
  },
  {
    to: "/connections",
    iconSrc: connectionsIcon,
    iconSrcActive: connectionsIconActive,
    labelKey: "sidebar.connections",
    flagKey: "connections",
  },
];

const SideMenu: React.FC<SideMenuProps> = ({
  onChangeLayoutMode,
  isOpen = false,
  onClose = () => {},
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const { isInstalled, isInstallable, installPWA, isStandalone } = usePWA();
  const { hasFlag } = useFeatureFlags();

  const visibleNavItems = navItems.filter((item) =>
    item.flagKey ? hasFlag(item.flagKey) : true,
  );

  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem("theme") === "dark";
  });

  useEffect(() => {
    const handler = (e: Event) => {
      setIsDarkMode((e as CustomEvent).detail.isDark);
    };
    window.addEventListener("appThemeChange", handler);
    return () => window.removeEventListener("appThemeChange", handler);
  }, []);

  const ICON_BUTTON_SIZE = "clamp(40px, 6vh, 52px)";
  const ICON_IMG_SIZE = "clamp(28px, 5vh, 40px)";
  const ICON_GAP = "clamp(0.4rem, 1.6vh, 1rem)";
  const RADIUS = "0.5rem";

  const isLightMode = theme.palette.mode === "light";
  const SIDEBAR_BG = isDarkMode
    ? "rgba(18, 18, 18, 0.98)"
    : "linear-gradient(180deg, #93F090 0%, #60BFB7 100%)";
  const HOVER_BG = isDarkMode
    ? "rgba(255, 255, 255, 0.08)"
    : isLightMode
      ? "rgba(255, 255, 255, 0.2)"
      : "rgba(255, 255, 255, 0.1)";
  const ACTIVE_BG = isDarkMode ? "rgba(255, 255, 255, 0.15)" : "#FFFFFF";
  const ACTIVE_BORDER = isDarkMode
    ? "1px solid rgba(255, 255, 255, 0.55)"
    : "none";
  const ICON_COLOR = isDarkMode ? "#e0e0e0" : isLightMode ? "#1a5f3a" : "white";

  // "md" in MUI defaults to 900px (see: https://mui.com/material-ui/customization/breakpoints/)
  // For 1024px, use 'down("lg")' since "lg" is 1200px, so 'down("lg")' means <1200px, or use a custom value
  // Examples:
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  ); // <900px
  const isTablet = useMediaQuery((theme: Theme) =>
    theme.breakpoints.between("md", "lg"),
  ); // 900px <= width < 1200px
  const isLessThan1024 = useMediaQuery("(max-width:1023.95px)"); // Explicit 1024px cutoff (width < 1024px)

  const isActive = (path: string) => location.pathname === path;

  const iconWrapperSx = {
    justifyContent: "center",
    alignItems: "center",
    padding: "0.25rem",
    borderRadius: RADIUS,
    transition: "background-color 0.3s ease",
    "&:hover": { backgroundColor: HOVER_BG },
  };

  const listItemIconSx = {
    color: ICON_COLOR,
    fontSize: 24,
    display: "flex",
    justifyContent: "center",
  };

  type IconItemProps = {
    to: string;
    labelKey: string;
    tooltipPlacement?: "right" | "top" | "bottom" | "left";
    iconSrc?: string;
    iconSrcActive?: string;
    icon?: React.ElementType;
  };

  const IconItem: React.FC<IconItemProps> = ({
    to,
    labelKey,
    tooltipPlacement = "right",
    iconSrc,
    iconSrcActive,
    icon: IconComp,
  }) => {
    const active = isActive(to);
    const currentIcon = active && iconSrcActive ? iconSrcActive : iconSrc;

    return (
      <Tooltip
        title={t(labelKey)}
        placement={tooltipPlacement}
        arrow
        slotProps={{ popper: { style: { zIndex: 10000 } } }}
      >
        <ListItem
          component={Link}
          to={to}
          sx={{
            ...iconWrapperSx,
            width: ICON_BUTTON_SIZE,
            height: ICON_BUTTON_SIZE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: active ? ACTIVE_BG : undefined,
            border: active ? ACTIVE_BORDER : "1px solid transparent",
            "&:hover": {
              backgroundColor: active ? ACTIVE_BG : HOVER_BG,
            },
          }}
        >
          <ListItemIcon sx={listItemIconSx}>
            {currentIcon ? (
              <img
                src={currentIcon}
                alt={`${t(labelKey)} icon`}
                style={{
                  width: ICON_IMG_SIZE,
                  height: ICON_IMG_SIZE,
                  objectFit: "contain",
                }}
              />
            ) : IconComp ? (
              <IconComp sx={{ fontSize: ICON_IMG_SIZE }} />
            ) : null}
          </ListItemIcon>
        </ListItem>
      </Tooltip>
    );
  };

  // PWA Download Button Component
  const PWADownloadButton: React.FC<{
    tooltipPlacement?: "right" | "top" | "bottom" | "left";
  }> = ({ tooltipPlacement = "right" }) => {
    const handlePWAClick = async () => {
      if (isInstalled) {
        try {
          const appUrl = `${window.location.origin}?standalone=true&t=${Date.now()}`;
          window.open(appUrl, "_blank", "noopener,noreferrer");
        } catch (error) {
          window.location.href = `${window.location.origin}?standalone=true`;
        }
      } else if (isInstallable) {
        await installPWA();
      }
    };

    const tooltipText = isInstalled
      ? t("Open Jesss Platform") || "Abrir Jesss Platform"
      : t("Download Jesss Platform") || "Baixar Jesss Platform";

    // Don't show if running as PWA
    if (isStandalone) {
      return null;
    }

    // Show only if installable or installed
    if (!isInstallable && !isInstalled) {
      return null;
    }

    return (
      <Tooltip
        title={tooltipText}
        placement={tooltipPlacement}
        arrow
        slotProps={{ popper: { style: { zIndex: 10000 } } }}
      >
        <ListItem
          onClick={handlePWAClick}
          sx={{
            ...iconWrapperSx,
            width: ICON_BUTTON_SIZE,
            height: ICON_BUTTON_SIZE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <ListItemIcon sx={listItemIconSx}>
            <img
              src={downloadIcon}
              alt="Download icon"
              style={{
                width: ICON_IMG_SIZE,
                height: ICON_IMG_SIZE,
                objectFit: "contain",
              }}
            />
          </ListItemIcon>
        </ListItem>
      </Tooltip>
    );
  };

  // --------------------------
  // Layout DESKTOP (sidebar)
  // --------------------------
  const desktopMenu = (
    <Box
      sx={{
        width: "4.6875rem",
        minHeight: "100dvh",
        background: SIDEBAR_BG,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0.75rem",
        borderRight: isDarkMode
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid #000",
        boxSizing: "border-box",
        zIndex: 9998,
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          marginBottom: ICON_GAP,
        }}
      >
        <Link
          to={hasFlag("chat_jess") ? "/chat-jess" : "/channel-session"}
          style={{ display: "flex", boxShadow: "none", filter: "none" }}
        >
          <img
            src={logoJess}
            alt="Logo Jess"
            style={{
              cursor: "pointer",
              width: ICON_IMG_SIZE,
              height: ICON_IMG_SIZE,
              objectFit: "contain",
              boxShadow: "none",
              filter: "none",
            }}
          />
        </Link>
      </Box>

      <Box
        sx={{
          mt: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: ICON_GAP,
          width: "100%",
        }}
      >
        {/* Ícone Chat Jess */}
        {hasFlag("chat_jess") && (
          <IconItem
            to="/chat-jess"
            iconSrc={chatJessIcon}
            iconSrcActive={chatJessIconActive}
            labelKey="sidebar.chatJess"
            tooltipPlacement="right"
          />
        )}

        {visibleNavItems.map((item) => (
          <IconItem key={item.labelKey} {...item} tooltipPlacement="right" />
        ))}

        {isSuperAdminRole() && (
          <IconItem
            to="/superadmin"
            icon={AdminPanelSettingsIcon}
            labelKey="sidebar.superAdmin"
            tooltipPlacement="right"
          />
        )}

        {/* Account - Profile Dropdown Menu */}
        <ProfileDropdownMenu onSideMenuClose={onClose} />

        {/* Download Jesss Platform - PWA */}
        <PWADownloadButton tooltipPlacement="right" />
      </Box>
    </Box>
  );

  // Bottom bar removido - usando apenas drawer lateral no mobile

  // --------------------------
  // Layout DRAWER LATERAL (mobile quando aberto)
  // --------------------------
  const drawerMenu = (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "280px",
          background: SIDEBAR_BG,
          borderRight: isDarkMode
            ? "1px solid rgba(255, 255, 255, 0.08)"
            : "1px solid rgba(0, 0, 0, 0.1)",
          paddingTop: "60px", // Espaço para a navbar
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "1.5rem 1rem",
          height: "100%",
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            marginBottom: "2rem",
          }}
        >
          <Link
            to={hasFlag("chat_jess") ? "/chat-jess" : "/channel-session"}
            style={{ display: "flex" }}
            onClick={onClose}
          >
            <img
              src={logoJess}
              alt="Logo Jess"
              style={{
                cursor: "pointer",
                width: "60px",
                height: "60px",
                objectFit: "contain",
              }}
            />
          </Link>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: "1rem",
            width: "100%",
          }}
        >
          {/* Chat Jess */}
          {hasFlag("chat_jess") && (
            <Link
              to="/chat-jess"
              onClick={onClose}
              style={{
                textDecoration: "none",
                padding: "12px 16px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                backgroundColor: isActive("/chat-jess")
                  ? ACTIVE_BG
                  : "transparent",
                borderLeft:
                  isActive("/chat-jess") && isDarkMode
                    ? "3px solid rgba(255, 255, 255, 0.85)"
                    : "3px solid transparent",
                transition: "all 0.2s ease",
              }}
            >
              <img
                src={isActive("/chat-jess") ? chatJessIconActive : chatJessIcon}
                alt="Chat Jess"
                style={{ width: "28px", height: "28px" }}
              />
              <span style={{ color: ICON_COLOR, fontWeight: 500 }}>
                {t("sidebar.chatJess")}
              </span>
            </Link>
          )}

          {visibleNavItems.map((item) => (
            <Link
              key={item.labelKey}
              to={item.to}
              onClick={onClose}
              style={{
                textDecoration: "none",
                padding: "12px 16px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                backgroundColor: isActive(item.to) ? ACTIVE_BG : "transparent",
                borderLeft:
                  isActive(item.to) && isDarkMode
                    ? "3px solid rgba(255, 255, 255, 0.85)"
                    : "3px solid transparent",
                transition: "all 0.2s ease",
              }}
            >
              <img
                src={
                  isActive(item.to) && item.iconSrcActive
                    ? item.iconSrcActive
                    : item.iconSrc
                }
                alt={t(item.labelKey)}
                style={{ width: "28px", height: "28px" }}
              />
              <span style={{ color: ICON_COLOR, fontWeight: 500 }}>
                {t(item.labelKey)}
              </span>
            </Link>
          ))}
          {isSuperAdminRole() && (
            <Link
              to="/superadmin"
              onClick={onClose}
              style={{
                textDecoration: "none",
                padding: "12px 16px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                backgroundColor: isActive("/superadmin")
                  ? ACTIVE_BG
                  : "transparent",
                borderLeft:
                  isActive("/superadmin") && isDarkMode
                    ? "3px solid rgba(255, 255, 255, 0.85)"
                    : "3px solid transparent",
                transition: "all 0.2s ease",
              }}
            >
              <AdminPanelSettingsIcon
                sx={{ width: "28px", height: "28px", color: ICON_COLOR }}
              />
              <span style={{ color: ICON_COLOR, fontWeight: 500 }}>
                {t("sidebar.superAdmin")}
              </span>
            </Link>
          )}
        </Box>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Account e Download */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            width: "100%",
            marginBottom: "1rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "12px 16px",
              borderRadius: "8px",
              gap: "12px",
              transition: "all 0.2s ease",
            }}
          >
            <ProfileDropdownMenu onSideMenuClose={onClose} />
            {/* <span style={{ color: ICON_COLOR, fontWeight: 500 }}>{t("profileTooltip")}</span> */}
          </Box>
          {!isStandalone && (isInstallable || isInstalled) && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <PWADownloadButton tooltipPlacement="top" />
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <>
      {isLessThan1024
        ? // Apenas drawer lateral no mobile, sem bottom bar
          drawerMenu
        : desktopMenu}
    </>
  );
};

export default SideMenu;
